import { Host, forceUpdate, h } from "@stencil/core";
import { FIXED_SIZES_SUM_CUSTOM_VAR, fixAndUpdateLayoutModel, getMousePosition, sizesToGridTemplate, updateComponentsAndDragBar } from "./utils";
import { ROOT_VIEW, addCursorInDocument, isRTL, removePointerEventsInDocumentBody, resetCursorInDocument, resetPointerEventsInDocumentBody } from "../../common/utils";
import { NO_FIXED_SIZES_TO_UPDATE, removeItem } from "./remove-item";
import { addSiblingLeaf } from "./add-sibling-item";
import { SyncWithRAF } from "../../common/sync-with-frames";
import { LAYOUT_SPLITTER_PARTS_DICTIONARY } from "../../common/reserved-names";
const GRID_TEMPLATE_DIRECTION_CUSTOM_VAR = "--ch-layout-splitter__distribution";
const STICKY_BLOCK_START_PROP = "inset-block-start";
const STICKY_BLOCK_END_PROP = "inset-block-end";
const STICKY_INLINE_START_PROP = "inset-inline-start";
const STICKY_INLINE_END_PROP = "inset-inline-end";
const DIRECTION_CLASS = (direction) => `group direction--${direction}`;
const TEMPLATE_STYLE = (items, itemsInfo, fixedSizesSum, sticky) => ({
    [GRID_TEMPLATE_DIRECTION_CUSTOM_VAR]: sizesToGridTemplate(items, itemsInfo, items.length - 1),
    [FIXED_SIZES_SUM_CUSTOM_VAR]: `${fixedSizesSum}px`,
    [STICKY_BLOCK_START_PROP]: sticky?.blockStart,
    [STICKY_BLOCK_END_PROP]: sticky?.blockEnd,
    [STICKY_INLINE_START_PROP]: sticky?.inlineStart,
    [STICKY_INLINE_END_PROP]: sticky?.inlineEnd
});
const getAriaControls = (layoutItems, index) => `${layoutItems[index].id} ${layoutItems[index + 1].id}`;
// Key codes
const ARROW_UP = "ArrowUp";
const ARROW_RIGHT = "ArrowRight";
const ARROW_DOWN = "ArrowDown";
const ARROW_LEFT = "ArrowLeft";
/**
 * @part bar - The bar that divides two columns or two rows
 */
export class ChLayoutSplitter {
    constructor() {
        this.dragging = false;
        this.barAccessibleName = "Resize";
        this.dragBarDisabled = false;
        this.incrementWithKeyboard = 2;
        this.model = {
            id: "root",
            direction: "columns",
            items: []
        };
    }
    // Sync computations with frames
    #dragRAF; // Don't allocate memory until needed when dragging
    #mouseDownInfo;
    #lastMousePosition;
    #newMousePosition;
    #fixedSizesSumRoot;
    // Distribution of elements
    #itemsInfo = new Map();
    el;
    modelChanged(newModel) {
        this.#updateLayoutInfo(newModel);
    }
    /**
     * Schedules a new render of the control even if no state changed.
     */
    async refreshLayout() {
        forceUpdate(this);
    }
    /**
     *
     */
    async addSiblingLeaf(parentGroup, siblingItem, placedInTheSibling, leafInfo, takeHalfTheSpaceOfTheSiblingItem) {
        const result = addSiblingLeaf(parentGroup, siblingItem, placedInTheSibling, leafInfo, this.#itemsInfo, takeHalfTheSpaceOfTheSiblingItem);
        if (result.success) {
            const fixedSizesSumIncrement = result.fixedSizesSumIncrement;
            // The fixesSizesSum of the parent must be updated
            if (fixedSizesSumIncrement) {
                const parentItem = this.#itemsInfo.get(parentGroup).parentItem;
                if (parentItem === ROOT_VIEW) {
                    this.#fixedSizesSumRoot += fixedSizesSumIncrement;
                }
                else {
                    this.#itemsInfo.get(parentItem.id).fixedSizesSum +=
                        fixedSizesSumIncrement;
                }
            }
            // Queue re-renders
            forceUpdate(this);
        }
        return result;
    }
    /**
     * Removes the item that is identified by the given ID.
     * The layout is rearranged depending on the state of the removed item.
     */
    async removeItem(itemId) {
        const parentItem = this.#itemsInfo.get(itemId).parentItem;
        const result = removeItem(itemId, this.#itemsInfo);
        if (result.success) {
            const fixedSizesSumDecrement = result.fixedSizesSumDecrement;
            // The fixesSizesSum of the parent must be updated
            if (fixedSizesSumDecrement !== NO_FIXED_SIZES_TO_UPDATE) {
                if (parentItem === ROOT_VIEW) {
                    this.#fixedSizesSumRoot -= fixedSizesSumDecrement;
                }
                else {
                    this.#itemsInfo.get(parentItem.id).fixedSizesSum -=
                        fixedSizesSumDecrement;
                }
            }
            // Queue re-renders
            forceUpdate(this);
        }
        return result;
    }
    #handleBarDrag = (event) => {
        event.preventDefault();
        this.#newMousePosition = getMousePosition(event, this.#mouseDownInfo.direction);
        this.#mouseDownInfo.mouseEvent = event;
        this.#dragRAF.perform(this.#handleBarDragRAF);
    };
    #handleBarDragRAF = (incrementInPx) => {
        updateComponentsAndDragBar(this.#mouseDownInfo, this.#itemsInfo, incrementInPx ?? this.#newMousePosition - this.#lastMousePosition, // Increment in px
        GRID_TEMPLATE_DIRECTION_CUSTOM_VAR);
        // Sync new position with last
        this.#lastMousePosition = this.#newMousePosition;
    };
    // Handler to remove mouse down
    #removeMouseMoveHandler = () => {
        document.removeEventListener("mousemove", this.#handleBarDrag, {
            capture: true
        });
    };
    // Remove mousemove and mouseup handlers when mouseup
    #mouseUpHandler = () => {
        this.dragging = false;
        // Cancel RAF to prevent access to undefined references
        if (this.#dragRAF) {
            this.#dragRAF.cancel();
        }
        // Remove handlers and state after finishing the resize
        this.#removeMouseMoveHandler();
        resetCursorInDocument();
        // Add again pointer-events
        resetPointerEventsInDocumentBody();
        document.removeEventListener("mouseup", this.#mouseUpHandler, {
            capture: true
        });
    };
    #initializeDragBarValuesForResizeProcessing = (direction, index, layoutItems, event) => {
        // Initialize the values needed for drag processing
        const dragBar = event.target;
        const dragBarContainer = dragBar.parentElement;
        this.#mouseDownInfo = {
            container: dragBarContainer,
            containerSize: direction === "columns"
                ? dragBarContainer.clientWidth
                : dragBarContainer.clientHeight,
            direction: direction,
            dragBar: dragBar,
            fixedSizesSumRoot: this.#fixedSizesSumRoot,
            itemStartId: layoutItems[index].id,
            itemEndId: layoutItems[index + 1].id,
            layoutItems: layoutItems,
            mouseEvent: undefined, // MouseEvent is initialized as undefined, since this object is used for the keyboard event
            RTL: isRTL()
        };
    };
    #mouseDownHandler = (direction, index, layoutItems) => (event) => {
        this.dragging = true;
        // Necessary to prevent selecting the inner image (or other elements) of
        // the bar item when the mouse is down
        event.preventDefault();
        this.#dragRAF ??= new SyncWithRAF();
        this.#initializeDragBarValuesForResizeProcessing(direction, index, layoutItems, event);
        // Set mouse cursor in the document
        addCursorInDocument(direction === "columns" ? "ew-resize" : "ns-resize");
        // Remove pointer-events during drag
        removePointerEventsInDocumentBody();
        // Mouse position
        const currentMousePosition = getMousePosition(event, direction);
        this.#lastMousePosition = currentMousePosition;
        this.#newMousePosition = currentMousePosition; // Also updated in mouse move
        // Add listeners
        document.addEventListener("mousemove", this.#handleBarDrag, {
            capture: true
        });
        document.addEventListener("mouseup", this.#mouseUpHandler, {
            capture: true,
            passive: true
        });
    };
    #handleResize = (direction, index, layoutItems) => (event) => {
        if ((direction === "rows" &&
            event.code !== ARROW_UP &&
            event.code !== ARROW_DOWN) ||
            (direction === "columns" &&
                event.code !== ARROW_LEFT &&
                event.code !== ARROW_RIGHT)) {
            return;
        }
        // Prevent scroll
        event.preventDefault();
        this.#initializeDragBarValuesForResizeProcessing(direction, index, layoutItems, event);
        const positiveIncrement = event.code === ARROW_RIGHT || event.code === ARROW_DOWN;
        this.#handleBarDragRAF(positiveIncrement
            ? this.incrementWithKeyboard
            : -this.incrementWithKeyboard);
    };
    #renderItem = (direction, lastComponentIndex, layoutItems, item, index) => {
        const itemUIModel = this.#itemsInfo.get(item.id);
        const isSticky = !!item.sticky;
        return [
            item.items ? (h("div", { id: item.id, class: {
                    [DIRECTION_CLASS(item.direction)]: true,
                    sticky: isSticky
                }, part: item.id, style: TEMPLATE_STYLE(item.items, this.#itemsInfo, itemUIModel.fixedSizesSum, item.sticky) }, this.#renderItems(item.direction, item.items, item.items.length - 1))) : (h("div", { id: item.id, class: { leaf: true, sticky: isSticky }, style: isSticky
                    ? {
                        [STICKY_BLOCK_START_PROP]: item.sticky?.blockStart,
                        [STICKY_BLOCK_END_PROP]: item.sticky?.blockEnd,
                        [STICKY_INLINE_START_PROP]: item.sticky?.inlineStart,
                        [STICKY_INLINE_END_PROP]: item.sticky?.inlineEnd
                    }
                    : undefined }, h("slot", { key: item.id, name: item.id }))),
            item.dragBar?.hidden !== true && index !== lastComponentIndex && (h("div", {
                // - - - Accessibility - - -
                role: "separator", "aria-controls": getAriaControls(layoutItems, index), "aria-disabled": this.dragBarDisabled ? "true" : null, "aria-label": this.barAccessibleName, "aria-orientation": direction === "columns" ? "vertical" : "horizontal", "aria-valuetext": itemUIModel.actualSize,
                // TODO: Add aria-valuenow
                title: this.barAccessibleName, tabindex: "0",
                // - - - - - - - - - - - - -
                class: "bar", part: item.dragBar?.part
                    ? `${LAYOUT_SPLITTER_PARTS_DICTIONARY.BAR} ${item.dragBar.part}`
                    : LAYOUT_SPLITTER_PARTS_DICTIONARY.BAR, style: item.dragBar?.size ? { "--size": `${item.dragBar.size}px` } : null, onKeyDown: !this.dragBarDisabled
                    ? this.#handleResize(direction, index, layoutItems)
                    : null, onMouseDown: !this.dragBarDisabled
                    ? this.#mouseDownHandler(direction, index, layoutItems)
                    : null
            }))
        ];
    };
    #renderItems = (direction, layoutItems, lastComponentIndex) => layoutItems.map((item, index) => this.#renderItem(direction, lastComponentIndex, layoutItems, item, index));
    #updateLayoutInfo = (layout) => {
        // Clear old information
        this.#itemsInfo.clear();
        if (layout?.items?.length > 0) {
            this.#fixedSizesSumRoot = fixAndUpdateLayoutModel(layout, this.#itemsInfo);
        }
    };
    connectedCallback() {
        this.#updateLayoutInfo(this.model);
    }
    disconnectedCallback() {
        // Defensive programming. Make sure all event listeners are removed correctly
        this.#mouseUpHandler();
    }
    render() {
        const layoutModel = this.model;
        if (layoutModel?.items == null) {
            return "";
        }
        return (
        // TODO: Add unit test for checking that the dialog is not closed when
        // a layout-splitter's bar is dragged
        // TODO: We should also add a dummy node in the ch-dialog or something
        // like that to not close the dialog when the mouse is released outside
        // of the ch-layout-splitter but inside the ch-dialog
        h(Host, { class: this.dragging ? "ch-layout-splitter--dragging" : undefined }, h("div", { class: DIRECTION_CLASS(layoutModel.direction), style: TEMPLATE_STYLE(layoutModel.items, this.#itemsInfo, this.#fixedSizesSumRoot) }, this.#renderItems(layoutModel.direction, layoutModel.items, layoutModel.items.length - 1))));
    }
    static get is() { return "ch-layout-splitter"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["layout-splitter.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["layout-splitter.css"]
        };
    }
    static get properties() {
        return {
            "barAccessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify the label for the drag bar.\nImportant for accessibility."
                },
                "attribute": "bar-accessible-name",
                "reflect": false,
                "defaultValue": "\"Resize\""
            },
            "dragBarDisabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the resize operation is disabled in all\ndrag bars. If `true`, the drag bars are disabled."
                },
                "attribute": "drag-bar-disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "incrementWithKeyboard": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the resizing increment (in pixel) that is applied when using the\nkeyboard to resize a drag bar."
                },
                "attribute": "increment-with-keyboard",
                "reflect": false,
                "defaultValue": "2"
            },
            "model": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "LayoutSplitterModel",
                    "resolved": "{ id: \"root\"; direction: LayoutSplitterDirection; items: LayoutSplitterItemModel[]; }",
                    "references": {
                        "LayoutSplitterModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/layout-splitter/types.ts::LayoutSplitterModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the list of component that are displayed. Each component will be\nseparated via a drag bar."
                },
                "defaultValue": "{\n    id: \"root\",\n    direction: \"columns\",\n    items: []\n  }"
            }
        };
    }
    static get states() {
        return {
            "dragging": {}
        };
    }
    static get methods() {
        return {
            "refreshLayout": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Schedules a new render of the control even if no state changed.",
                    "tags": []
                }
            },
            "addSiblingLeaf": {
                "complexType": {
                    "signature": "(parentGroup: string, siblingItem: string, placedInTheSibling: \"before\" | \"after\", leafInfo: LayoutSplitterLeafModel, takeHalfTheSpaceOfTheSiblingItem: boolean) => Promise<LayoutSplitterItemAddResult>",
                    "parameters": [{
                            "name": "parentGroup",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "siblingItem",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "placedInTheSibling",
                            "type": "\"after\" | \"before\"",
                            "docs": ""
                        }, {
                            "name": "leafInfo",
                            "type": "{ id: string; dragBar?: LayoutSplitterDragBarConfig; fixedOffsetSize?: number; size: LayoutSplitterSize; minSize?: `${number}px`; sticky?: LayoutSplitterSticky; }",
                            "docs": ""
                        }, {
                            "name": "takeHalfTheSpaceOfTheSiblingItem",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "LayoutSplitterItemAddResult": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/layout-splitter/types.ts::LayoutSplitterItemAddResult"
                        },
                        "LayoutSplitterLeafModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/layout-splitter/types.ts::LayoutSplitterLeafModel"
                        },
                        "GroupExtended": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/layout-splitter/types.ts::GroupExtended"
                        }
                    },
                    "return": "Promise<LayoutSplitterItemAddResult>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "removeItem": {
                "complexType": {
                    "signature": "(itemId: string) => Promise<LayoutSplitterItemRemoveResult>",
                    "parameters": [{
                            "name": "itemId",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "LayoutSplitterItemRemoveResult": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/layout-splitter/types.ts::LayoutSplitterItemRemoveResult"
                        },
                        "GroupExtended": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/layout-splitter/types.ts::GroupExtended"
                        }
                    },
                    "return": "Promise<LayoutSplitterItemRemoveResult>"
                },
                "docs": {
                    "text": "Removes the item that is identified by the given ID.\nThe layout is rearranged depending on the state of the removed item.",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "model",
                "methodName": "modelChanged"
            }];
    }
}
